// Central access point for auth-token storage. Supports two modes:
//
//  - shared (default): tokens live in localStorage, visible to every tab
//    of the same browser profile, kept in sync by the 'storage' event
//    handler in AuthContext.
//  - per-tab: tokens live in sessionStorage only, so two windows of the
//    same browser profile can be logged in as different users side by
//    side. Opt in on the login page.
//
// Consumers (AuthContext, apiService interceptors) should read/write
// through these helpers so the two modes stay consistent and nothing
// writes directly to localStorage any more.

const ACCESS_KEY = 'auth_token';
const REFRESH_KEY = 'refresh_token';
const IMPERSONATION_KEY = 'impersonation';

// Read-only "View as user" handoff. The admin's Users pane opens a NEW tab with
// the impersonation token in the URL hash (a fragment is never sent to the
// server or written to server logs). We consume it BEFORE React/AuthContext
// mounts: write the token to sessionStorage (per-tab, so the admin's shared
// localStorage session in other tabs is untouched), record the impersonation
// banner state, and strip the token from the URL. Returns true if a handoff was
// consumed. Safe to call on every page load — a no-op without the hash param.
export function consumeImpersonationHandoff() {
  try {
    const hash = window.location.hash || '';
    const m = hash.match(/(?:^#|&)imp=([^&]+)/);
    if (!m) return false;
    let payload;
    try { payload = JSON.parse(decodeURIComponent(m[1])); } catch { return false; }
    if (!payload || !payload.t) return false;
    sessionStorage.setItem(ACCESS_KEY, payload.t);
    // A short-lived impersonation token has NO refresh token — it simply expires.
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify({
      name: payload.n || payload.e || 'user',
      email: payload.e || '',
      expiresAt: payload.x || null,
    }));
    // Remove the token from the address bar / history.
    const clean = (window.location.pathname || '/') + (window.location.search || '');
    window.history.replaceState(null, '', clean);
    return true;
  } catch {
    return false;
  }
}

// Current tab's impersonation banner state ({ name, email, expiresAt }) or null.
export function getImpersonation() {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// End the impersonation session in THIS tab (clears the per-tab token + banner
// state). The admin's shared localStorage session is left intact.
export function endImpersonation() {
  try {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch { /* ignore */ }
}

export function isPerTab() {
  try {
    return !!sessionStorage.getItem(ACCESS_KEY);
  } catch {
    return false;
  }
}

export function getAccessToken() {
  try {
    const s = sessionStorage.getItem(ACCESS_KEY);
    if (s) return s;
  } catch { /* ignore */ }
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch { return null; }
}

export function getRefreshToken() {
  try {
    const s = sessionStorage.getItem(REFRESH_KEY);
    if (s) return s;
  } catch { /* ignore */ }
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch { return null; }
}

// Write tokens to whichever scope is active. Deliberately does NOT touch
// the opposite store: a per-tab login must not fire a localStorage
// write/remove (which would broadcast a 'storage' event to every other
// tab and log them out), and a shared login must not clobber a per-tab
// tab's sessionStorage (sessionStorage is tab-scoped so this is
// cosmetic, but the symmetry keeps the invariant clean).
export function setTokens({ accessToken, refreshToken }, { perTab = false } = {}) {
  const store = perTab ? sessionStorage : localStorage;
  try {
    if (accessToken != null) store.setItem(ACCESS_KEY, accessToken);
    if (refreshToken != null) store.setItem(REFRESH_KEY, refreshToken);
  } catch { /* storage quota / disabled — callers see null on read */ }
}

// Update only the access token while preserving the scope the refresh
// token is currently in. Used by the axios refresh interceptor.
export function updateAccessToken(accessToken, newRefreshToken = null) {
  const perTab = isPerTab();
  const store = perTab ? sessionStorage : localStorage;
  try {
    store.setItem(ACCESS_KEY, accessToken);
    if (newRefreshToken != null) {
      store.setItem(REFRESH_KEY, newRefreshToken);
    }
  } catch { /* ignore */ }
}

// Clear tokens for this tab. By default only the active scope is
// cleared — a per-tab logout must not remove another window's
// shared-session token (and vice versa: sessionStorage is tab-scoped
// so clearing it from a shared-mode logout only affects the current
// tab anyway). Callers that need a belt-and-braces wipe (e.g. 401
// dead-ends where we want to force the user back to /login
// regardless of scope) can pass { allScopes: true }.
export function clearTokens({ allScopes = false } = {}) {
  const perTab = isPerTab();
  try {
    if (perTab || allScopes) {
      sessionStorage.removeItem(ACCESS_KEY);
      sessionStorage.removeItem(REFRESH_KEY);
    }
  } catch { /* ignore */ }
  try {
    if (!perTab || allScopes) {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
    }
  } catch { /* ignore */ }
}
